import { ReactElement } from 'react';
import { CoreAssortmentToggleFilter } from '@client/catalog/components/CoreAssortmentToggleFilter';
import { useCatalogFacetFiltersActions, useSearchData } from '@client/catalog/hooks';
import {
  FACET_FILTERS,
  getIsCoreAssortmentFilterActive,
  getIsCoreAssortmentFilterAvailable,
  translateFilterName,
} from '@client/catalog/utils/facets';
import { translateFilterOption } from '@client/catalog/utils/filters';
import { useTranslate } from '@client/i18n/hooks';
import { ResultsActiveFilters } from './ResultsActiveFilters';
import { ActiveFilter, Props } from './ResultsActiveFiltersContainer.types';

export const ResultsActiveFiltersContainer = ({
  activeFilters,
  allowCoreAssortment,
  onFilterChange,
}: Props): ReactElement => {
  const t = useTranslate();
  const { data } = useSearchData();
  const catalogFilters = data?.filters || [];
  const {
    handleFacetFilterRemove,
    handleFacetFiltersRemoveAll,
    handleFacetFilterCheckboxChange,
  } = useCatalogFacetFiltersActions(onFilterChange);

  const isCoreAssortmentFilterAvailable = getIsCoreAssortmentFilterAvailable(catalogFilters);
  const isCoreAssortmentFilterActive = getIsCoreAssortmentFilterActive(catalogFilters);

  const getActiveFilters = (): ActiveFilter[] => {
    const result: ActiveFilter[] = [];

    Object.keys(activeFilters).forEach((filterField) => {
      const filter = activeFilters[filterField];
      const value = filter.value;

      if (Array.isArray(value) && (value[0] !== FACET_FILTERS.customerAssortment)) {
        //handle selectable filters
        value.forEach((v) => {
          result.push({
            label: `${translateFilterName(t, filterField, filter.name)}: ${translateFilterOption(
              t,
              filterField,
              v,
            )}`,
            value: v,
            section: filterField,
          });
        });
      } else if (value.min !== undefined || value.max !== undefined) {
        //handle range filters
        const from = value.min !== undefined ? `${t('from')}: ${value.min}` : '';
        const to = value.max !== undefined ? `${t('to')}: ${value.max}` : '';
        result.push({
          label: `${filter.name} ${from} ${to}`,
          value: `${value.min}-${value.max}`,
          section: filterField,
        });
      }
    });

    return result;
  };

  return (
    <ResultsActiveFilters
      activeFilters={getActiveFilters()}
      onRemoveAllFilters={handleFacetFiltersRemoveAll}
      onRemoveFilter={handleFacetFilterRemove}
    >
      {allowCoreAssortment && isCoreAssortmentFilterAvailable && (
        <CoreAssortmentToggleFilter
          isChecked={isCoreAssortmentFilterActive}
          onFacetFilterCheckboxChange={handleFacetFilterCheckboxChange}
        />
      )}
    </ResultsActiveFilters>
  );
};
